import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ReactPlaceholder from 'react-placeholder';
import { connect, useDispatch } from 'react-redux';
import { useNavigate, useLocation, useParams, useOutletContext } from 'react-router-dom';
import { deleteDevice, fetchDeviceConfig } from 'commons/src/actions/DeviceActions';
import PrimaryButton from 'commons/src/components/buttons/PrimaryButton';
import PageWrapperContent from 'commons/src/components/containers/PageWrapperContent';
import DeviceInfo from 'commons/src/components/device/DeviceInfo';
import CollapsableSectionHeader from 'commons/src/components/headers/CollapsableSectionHeader';
import { mediumFormLoader } from 'commons/src/components/placeholder';
import { paths } from 'commons/src/constants';
import DeleteModal from 'commons/src/features/devicePage/dropdownOptions/DeleteModal';
import { hasFeatureToggle } from 'commons/src/features/featureToggle/filterOnFeatureToggles';
import { FeatureToggleEnum } from 'commons/src/models/commonEnums';
import { DeviceConfig, DeviceType } from 'commons/src/models/commonTypeScript';
import { Store } from 'commons/src/reducers';
import { CommonRequestType as RequestType } from 'commons/src/reducers/requestReducer';
import { DevicePageOutletContext } from '../DevicePage';
import DeviceThresholdsContent from '../DeviceThresholdsContent';
import BatterySettings from './BatterySettings';
import ChangeLocation from './ChangeLocation';
import styles from './DeviceSettings.module.scss';

export type Props = {
    loading: boolean;
    devices: { [serialNumber: string]: DeviceType };
    locationsLoading: boolean;
    demoMode: boolean;
    deviceConfigs: {
        [serialNumber: string]: DeviceConfig;
    };
    featureToggles: FeatureToggleEnum[];
};

export const DeviceSettingsComponent = ({
    loading,
    locationsLoading,
    devices,
    demoMode,
    deviceConfigs,
    featureToggles,
}: Props): React.ReactElement | null => {
    const { t: txt } = useTranslation();
    const { serialNumber } = useParams() as { serialNumber: string };
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const sensorThresholdsSectionRef = useRef<HTMLDivElement>(null);
    const {
        showThresholdLines,
        setShowThresholdLines,
        scaleGraphToThresholds,
        setScaleGraphToThresholds,
        showLegendOnLeft,
        setShowLegendOnLeft,
    } = useOutletContext<DevicePageOutletContext>();
    const secondaryOptionsDisabled = !showThresholdLines;
    const hasBatterySettings = deviceConfigs[serialNumber] && deviceConfigs[serialNumber].batteryMode;
    const selectedDevice = devices[serialNumber];
    const [deviceDetailsOpen, setDeviceDetailsOpen] = useState(false);
    const [deviceThresholdsOpen, setDeviceThresholdsOpen] = useState(false);
    const [batterySettingsOpen, setBatterySettingsOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    useEffect((): void => {
        if (!locationsLoading) {
            dispatch(fetchDeviceConfig(serialNumber, (selectedDevice && selectedDevice.segmentId) || 'latest'));
        }
    }, [locationsLoading, serialNumber, selectedDevice]);

    useEffect(() => {
        if (!loading && !selectedDevice) {
            navigate(`/${paths.devices}`);
        }
    }, [loading, selectedDevice, navigate]);

    useEffect(() => {
        if ((location.state as { scrollToSection?: string } | null)?.scrollToSection === 'sensor-thresholds') {
            setDeviceThresholdsOpen(true);
        }
    }, [location.state]);

    useEffect(() => {
        if (
            deviceThresholdsOpen &&
            (location.state as { scrollToSection?: string } | null)?.scrollToSection === 'sensor-thresholds'
        ) {
            const scrollAndClear = (): void => {
                sensorThresholdsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                navigate(location.pathname, { replace: true, state: {} });
            };
            const id = requestAnimationFrame(() => requestAnimationFrame(scrollAndClear));
            return (): void => cancelAnimationFrame(id);
        }
        return undefined;
    }, [deviceThresholdsOpen, location.state, location.pathname, navigate]);

    const onDeleteModalOpenClose = (): void => {
        setIsDeleteModalOpen(!isDeleteModalOpen);
    };

    const clickDeleteDevice = (): void => {
        if (demoMode) return;
        dispatch(deleteDevice(serialNumber, selectedDevice.locationId));
    };

    return (
        <div className="flex-column">
            <div className="page-wrapper__medium">
                <ReactPlaceholder ready={!loading} customPlaceholder={mediumFormLoader}>
                    {isDeleteModalOpen && (
                        <DeleteModal
                            isB2B={false}
                            isEndedSegment={false}
                            onDeleteDevice={clickDeleteDevice}
                            isModalOpen={isDeleteModalOpen}
                            onModalClose={onDeleteModalOpenClose}
                            segmentName={selectedDevice.segmentName}
                        />
                    )}
                    <div className="settings-details-container settings-details-container--center settings-details-container--margin">
                        <DeviceInfo
                            serialNumber={serialNumber}
                            deviceType={selectedDevice && selectedDevice.type}
                            segmentStart={selectedDevice && selectedDevice.segmentStart}
                            noMargin
                        />
                    </div>
                    <div className="settings-details-container settings-details-container--margin">
                        <CollapsableSectionHeader
                            openSection={setDeviceDetailsOpen}
                            headerIsOpen={deviceDetailsOpen}
                            headerText="DeviceSettings.DeviceSettings"
                            subtext="DeviceSettings.DeviceSettingsSubHeader"
                            loading={false}
                            testAttr="settings"
                        />
                        {deviceDetailsOpen && (
                            <ChangeLocation
                                goBack={(): void => setDeviceDetailsOpen(false)}
                                serialNumber={serialNumber}
                            />
                        )}
                    </div>
                    {hasFeatureToggle(FeatureToggleEnum.customThresholdsB2c, featureToggles) &&
                        setShowThresholdLines != null && (
                            <div
                                ref={sensorThresholdsSectionRef}
                                className="settings-details-container settings-details-container--margin"
                            >
                                <CollapsableSectionHeader
                                    openSection={setDeviceThresholdsOpen}
                                    headerIsOpen={deviceThresholdsOpen}
                                    headerText="CustomThresholds.SensorThresholds"
                                    subtext="CustomThresholds.DeviceThresholdsSubText"
                                    testAttr="sensor-thresholds"
                                />
                                {deviceThresholdsOpen && (
                                    <PageWrapperContent pageType="full">
                                        <div className="settings-details-container settings-details-container--margin">
                                            <div className={styles.toggleRow}>
                                                <span className={styles.toggleLabel}>{txt('ShowThresholdLines')}</span>
                                                <div
                                                    className={styles.togglePill}
                                                    role="group"
                                                    aria-label={txt('ShowThresholdLines')}
                                                >
                                                    <button
                                                        type="button"
                                                        className={classNames(styles.toggleSegment, {
                                                            [styles.selected]: showThresholdLines ?? false,
                                                        })}
                                                        onClick={(): void => setShowThresholdLines?.(true)}
                                                        data-testid="show-threshold-lines-on"
                                                    >
                                                        {txt('On')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={classNames(styles.toggleSegment, {
                                                            [styles.selected]: !(showThresholdLines ?? false),
                                                        })}
                                                        onClick={(): void => setShowThresholdLines?.(false)}
                                                        data-testid="show-threshold-lines-off"
                                                    >
                                                        {txt('Off')}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className={styles.toggleRow}>
                                                <span className={styles.toggleLabel}>
                                                    {txt('ScaleGraphToThresholds')}
                                                </span>
                                                <div
                                                    className={styles.togglePill}
                                                    role="group"
                                                    aria-label={txt('ScaleGraphToThresholds')}
                                                >
                                                    <button
                                                        type="button"
                                                        className={classNames(styles.toggleSegment, {
                                                            [styles.selected]: scaleGraphToThresholds ?? false,
                                                        })}
                                                        onClick={(): void => setScaleGraphToThresholds?.(true)}
                                                        disabled={secondaryOptionsDisabled}
                                                        data-testid="scale-graph-to-thresholds-on"
                                                    >
                                                        {txt('On')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={classNames(styles.toggleSegment, {
                                                            [styles.selected]: !(scaleGraphToThresholds ?? false),
                                                        })}
                                                        onClick={(): void => setScaleGraphToThresholds?.(false)}
                                                        disabled={secondaryOptionsDisabled}
                                                        data-testid="scale-graph-to-thresholds-off"
                                                    >
                                                        {txt('Off')}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className={styles.toggleRow}>
                                                <span className={styles.toggleLabel}>{txt('ShowLegendOnRight')}</span>
                                                <div
                                                    className={styles.togglePill}
                                                    role="group"
                                                    aria-label={txt('ShowLegendOnRight')}
                                                >
                                                    <button
                                                        type="button"
                                                        className={classNames(styles.toggleSegment, {
                                                            [styles.selected]: showLegendOnLeft ?? false,
                                                        })}
                                                        onClick={(): void => setShowLegendOnLeft?.(true)}
                                                        disabled={secondaryOptionsDisabled}
                                                        data-testid="show-legend-on-left-on"
                                                    >
                                                        {txt('On')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={classNames(styles.toggleSegment, {
                                                            [styles.selected]: !(showLegendOnLeft ?? false),
                                                        })}
                                                        onClick={(): void => setShowLegendOnLeft?.(false)}
                                                        disabled={secondaryOptionsDisabled}
                                                        data-testid="show-legend-on-left-off"
                                                    >
                                                        {txt('Off')}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <DeviceThresholdsContent
                                            serialNumber={serialNumber}
                                            readOnly
                                            active={deviceThresholdsOpen}
                                        />
                                    </PageWrapperContent>
                                )}
                            </div>
                        )}
                    {hasBatterySettings && (
                        <div className="settings-details-container settings-details-container--margin">
                            <CollapsableSectionHeader
                                openSection={setBatterySettingsOpen}
                                headerIsOpen={batterySettingsOpen}
                                headerText="DeviceSettings.BatteryLife"
                                subtext="DeviceSettingsConsumer.BatteryLifeDescription"
                                loading={false}
                                testAttr="battery"
                            />
                            {batterySettingsOpen && (
                                <BatterySettings
                                    serialNumber={serialNumber}
                                    onClose={(): void => setBatterySettingsOpen(false)}
                                />
                            )}
                        </div>
                    )}
                    {/* {deviceIsViewPlus && (
                        <div className="settings-details-container settings-details-container--margin">
                            <CollapsableSectionHeader
                                openSection={setDisplaySettingsOpen}
                                headerIsOpen={displaySettingsOpen}
                                headerText="DeviceSettings.Display"
                                subtext={txt('DeviceSettings.DisplaySubHeader', {
                                    deviceType: txt(mapDeviceType(DeviceTypeNames.viewPlus)),
                                })}
                                loading={false}
                                testAttr="display-settings"
                                translateSubText={false}
                            />
                            {displaySettingsOpen && (
                                <ViewPlusSettings // this component is no longer in use. Make sure to delete this as well when removing this. If starting to use this again tests for this component have been commented out and needs to be updated.
                                    goBack={(): void => setDisplaySettingsOpen(false)}
                                    serialNumber={serialNumber}
                                    publicUrl={selectedDevice.publicUrlPath}
                                />
                            )}
                        </div>
                    )} */}
                    <div className="change-location__form__buttons">
                        <PrimaryButton onClick={onDeleteModalOpenClose} color="alert" title="DeleteDevice" />
                    </div>
                </ReactPlaceholder>
            </div>
        </div>
    );
};

const mapStateToProps = (store: Store): Props => {
    const {
        devices: { devices },
        userSettings: { demoMode, featureToggles },
        deviceConfig: { deviceConfigs },
        locations: { loading: locationsLoading, locations },
        commonRequests: {
            [RequestType.FetchDeviceConfig]: { loading: configLoading },
        },
    } = store;

    return {
        devices,
        demoMode,
        deviceConfigs,
        loading: (locationsLoading && locations.length === 0) || configLoading,
        locationsLoading: locationsLoading && locations.length === 0,
        featureToggles: featureToggles || [],
    };
};

export default connect(mapStateToProps)(DeviceSettingsComponent);
